import { apiClient, ambassadorClient, API_ENDPOINTS, TIME_BUDGETS } from '../../../lib';
import type {
  CreateSiteFromTemplateRequest,
  SetupModalystProductsRequest,
  InstallAppsRequest,
  InstallAppsResponse,
  ResetStoreRequest,
  ResetStoreResponse,
  CreateSitelessMetasiteResponse,
  AttachTemplateRequest,
  AttachTemplateResponse,
  InstallStoresAppRequest,
  InstallStoresAppResponse,
} from '@common/api-types';
import type { CreateMetaSiteFromTemplateResponse } from '@wix/ambassador-metasite-api-v1-meta-site/types';
import {
  setupModalystProductsByCategory as setupModalystProductsByCategoryHttp,
  deleteAllProducts as deleteAllProductsHttp,
} from '@wix/ambassador-gg-store-setup-v1-store-setup-service/http';

export async function updateTheme(metaSiteId: string, themeKey?: string): Promise<void> {
  await apiClient.post(
    API_ENDPOINTS.site.updateTheme,
    {
      metaSiteId,
      themeKey,
    },
    {
      headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
    },
  );
}

export async function createSiteFromTemplate(category: string, lang?: string): Promise<string> {
  const request: CreateSiteFromTemplateRequest = { category, lang };

  const response = await apiClient.post<CreateMetaSiteFromTemplateResponse>(
    API_ENDPOINTS.site.createFromTemplate,
    request,
    {
      headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
    },
  );

  return response.metaSite?.metaSiteId || '';
}

export async function createSitelessMetasite(): Promise<string> {
  const response = await apiClient.post<CreateSitelessMetasiteResponse>(
    API_ENDPOINTS.site.createSitelessMetasite,
    {},
    {
      headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
    },
  );

  return response.metaSiteId || '';
}

export async function installStoresApp(metaSiteId: string): Promise<boolean> {
  const request: InstallStoresAppRequest = { metaSiteId };

  const response = await apiClient.post<InstallStoresAppResponse>(
    API_ENDPOINTS.site.installStoresApp,
    request,
    {
      headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
    },
  );

  return response.success || false;
}

export async function attachTemplate(metaSiteId: string, category: string, lang?: string): Promise<boolean> {
  const request: AttachTemplateRequest = { metaSiteId, category, lang };

  const response = await apiClient.post<AttachTemplateResponse>(API_ENDPOINTS.site.attachTemplate, request, {
    headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
  });

  return response.success || false;
}

export async function setupModalystProducts(metaSiteId: string, productList: string[]): Promise<void> {
  const request: SetupModalystProductsRequest = {
    productList,
    metaSiteId,
  };

  await apiClient.post(API_ENDPOINTS.storeSetup.setupModalystProducts, request, {
    headers: { 'x-time-budget': `${TIME_BUDGETS.long}` },
  });
}

export async function setupModalystProductsByCategory(category: string): Promise<void> {
  await ambassadorClient.request(
    setupModalystProductsByCategoryHttp({
      category,
    }),
  );
}

export async function deleteAllProducts(): Promise<void> {
  await ambassadorClient.request(deleteAllProductsHttp({}));
}

export async function installApps(metaSiteId: string, apps?: string[]): Promise<boolean> {
  const request: InstallAppsRequest = { metaSiteId, apps };

  const response = await apiClient.post<InstallAppsResponse>(API_ENDPOINTS.site.installApps, request, {
    headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
  });

  return response.success || false;
}

export async function resetStore(metaSiteId: string): Promise<boolean> {
  const request: ResetStoreRequest = { metaSiteId };

  const response = await apiClient.post<ResetStoreResponse>(API_ENDPOINTS.site.resetStore, request, {
    headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
  });

  return response.success || false;
}
